// 國碼對應顯示名稱，後端未定義的代碼直接顯示原始代碼
const NATIONALITY_NAMES = {
  JP: 'Japan',
  HK: 'Hong Kong',
  AU: 'Australia',
  MO: 'Macau',
  MY: 'Malaysia',
  CA: 'Canada',
  TW: 'Taiwan',
  KO: 'Korea',
  TH: 'Thailand',
  IN: 'India',
  BR: 'Brazil',
  ID: 'Indonesia'
}

export function getNationalityName(code) {
  return NATIONALITY_NAMES[code] || code
}

export function getNationalityOptions(codes = []) {
  return codes
    .map(code => ({ code, name: getNationalityName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// 各字元集「不允許」的字元（用來即時過濾掉），未列在此的代碼不做限制
const DISALLOWED_PATTERNS = {
  katakana: /[^゠-ヿㇰ-ㇿ]/g,
  kanji: /[^々〇㐀-䶿一-鿿豈-﫿]/g,
  traditionalChinese: /[^㐀-䶿一-鿿豈-﫿]/g,
  english: /[^A-Za-z .'-]/g,
  latin: /[^A-Za-zÀ-ÖØ-öø-ɏ .'-]/g,
  korean: /[^가-힣 ]/g,
  thai: /[^฀-๿ ]/g
}

// name：位置1（漢字欄，填母語）；romanization：位置2（カタカナ欄，填拼音/英文）
const NATIONALITY_FIELD_RULES = {
  JP: { name: 'kanji', romanization: 'katakana' },
  MY: { name: 'latin', romanization: 'english' },
  HK: { name: 'traditionalChinese', romanization: 'english' },
  AU: { name: 'english', romanization: 'english' },
  MO: { name: 'traditionalChinese', romanization: 'latin' },
  KO: { name: 'korean', romanization: 'english' },
  CA: { name: 'english', romanization: 'english' },
  TW: { name: 'traditionalChinese', romanization: 'english' },
  TH: { name: 'thai', romanization: 'english' },
  IN: { name: 'english', romanization: 'english' },
  BR: { name: 'latin', romanization: 'latin' },
  ID: { name: 'english', romanization: 'english' }
}

// 各國籍四個姓名欄位的標題與輸入格式（固定字串，不隨語系變動）
// nameLast/nameFirst：位置1（漢字欄）；romLast/romFirst：位置2（カタカナ欄）
const NATIONALITY_FIELD_LABELS = {
  JP: { nameLast: ['姓', '漢字'], nameFirst: ['名', '漢字'], romLast: ['姓', 'カタカナ'], romFirst: ['名', 'カタカナ'] },
  MY: { nameLast: ['Nama Keluarga', 'Malay'], nameFirst: ['Nama Pertama', 'Malay'], romLast: ['Last Name', 'English'], romFirst: ['First Name', 'English'] },
  HK: { nameLast: ['姓', '中文'], nameFirst: ['名', '中文'], romLast: ['Last Name', 'English'], romFirst: ['First Name', 'English'] },
  AU: { nameLast: ['Last Name', ''], nameFirst: ['First Name', ''], romLast: ['Middle Name 1', ''], romFirst: ['Middle Name 2', ''] },
  MO: { nameLast: ['姓', '中文'], nameFirst: ['名', '中文'], romLast: ['Apelido', 'Português'], romFirst: ['Nome', 'Português'] },
  KO: { nameLast: ['성', '한국어'], nameFirst: ['이름', '한국어'], romLast: ['Last Name', 'English'], romFirst: ['First Name', 'English'] },
  CA: { nameLast: ['Last Name', ''], nameFirst: ['First Name', ''], romLast: ['Middle Name 1', ''], romFirst: ['Middle Name 2', ''] },
  TW: { nameLast: ['姓', '中文'], nameFirst: ['名', '中文'], romLast: ['Last Name', 'English'], romFirst: ['First Name', 'English'] },
  TH: { nameLast: ['นามสกุล', 'ภาษาไทย'], nameFirst: ['ชื่อ', 'ภาษาไทย'], romLast: ['Last Name', 'English'], romFirst: ['First Name', 'English'] },
  IN: { nameLast: ['Last Name', ''], nameFirst: ['First Name', ''], romLast: ['Middle Name 1', ''], romFirst: ['Middle Name 2', ''] },
  BR: { nameLast: ['Last Name', ''], nameFirst: ['First Name', ''], romLast: ['Middle Name 1', ''], romFirst: ['Middle Name 2', ''] },
  ID: { nameLast: ['Last Name', ''], nameFirst: ['First Name', ''], romLast: ['Middle Name 1', ''], romFirst: ['Middle Name 2', ''] }
}

// 各國籍四個姓名欄位的填寫範例（固定字串，顯示在欄位標題下方）
const NATIONALITY_FIELD_EXAMPLES = {
  JP: { nameLast: '山田', nameFirst: '太郎', romLast: 'ヤマダ', romFirst: 'タロウ' },
  MY: { nameLast: 'Abdullah', nameFirst: 'Ahmad', romLast: 'Abdullah', romFirst: 'Ahmad' },
  HK: { nameLast: '陳', nameFirst: '大文', romLast: 'Chan', romFirst: 'Tai Man' },
  AU: { nameLast: 'Smith', nameFirst: 'John', romLast: 'William', romFirst: 'James' },
  MO: { nameLast: '陳', nameFirst: '大文', romLast: 'Silva', romFirst: 'João' },
  KO: { nameLast: '김', nameFirst: '민준', romLast: 'Kim', romFirst: 'Min-jun' },
  CA: { nameLast: 'Smith', nameFirst: 'John', romLast: 'William', romFirst: 'James' },
  TW: { nameLast: '陳', nameFirst: '大文', romLast: 'Chen', romFirst: 'Ta-Wen' },
  TH: { nameLast: 'สุขใจ', nameFirst: 'สมชาย', romLast: 'Sukjai', romFirst: 'Somchai' },
  IN: { nameLast: 'Sharma', nameFirst: 'Raj', romLast: 'Kumar', romFirst: 'Dev' },
  BR: { nameLast: 'Santos', nameFirst: 'Maria', romLast: 'Eduarda', romFirst: 'Silva' },
  ID: { nameLast: 'Santoso', nameFirst: 'Budi', romLast: 'Wijaya', romFirst: 'Kusuma' }
}

// 取得指定國籍四個姓名欄位的 { label, format, example }
// 未定義的國籍以 defaultLabels（i18n 預設標題）回退，不顯示格式與範例
export function getNameFields(nationality, defaultLabels) {
  const labels = NATIONALITY_FIELD_LABELS[nationality]
  const examples = NATIONALITY_FIELD_EXAMPLES[nationality] || {}
  return Object.keys(defaultLabels).reduce((fields, key) => {
    const [label, format] = labels ? labels[key] : [defaultLabels[key], '']
    fields[key] = { label, format, example: examples[key] || '' }
    return fields
  }, {})
}

// 位置1（漢字欄）非必填的國籍：部分日本人姓名沒有漢字
const NAME_OPTIONAL_NATIONALITIES = ['JP']

// 位置1（漢字欄）是否必填
export function isNameFieldRequired(nationality) {
  return !NAME_OPTIONAL_NATIONALITIES.includes(nationality)
}

// 依國籍與欄位即時過濾掉不允許的字元；未定義規則的國籍不做任何限制
export function sanitizeNameField(value, nationality, field) {
  if (!value) return value
  const rule = NATIONALITY_FIELD_RULES[nationality]
  if (!rule || !rule[field]) return value
  const pattern = DISALLOWED_PATTERNS[rule[field]]
  if (!pattern) return value
  return value.replace(pattern, '')
}
